import { SelfContainedCommand, CommandExecutionContext } from "../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../types/standardToolResults";
import { createFileLogger } from "../../utils/logger";
import useEntityStore from "../../stores/entityStore";
import { resolveEntityId } from "./entityIdResolver";

const log = createFileLogger('ENTITIES_UNGROUP');

export const entitiesUngroupCommand: SelfContainedCommand = {
  id: 'entities_ungroup',
  description: `Move all entities out of a folder and delete the folder itself.

**Use Cases:**
- Dissolve temporary groupings
- Flatten folder hierarchy
- Remove empty organizational structures
- Reorganize workspace`,
  autonomyLevel: 'ask',
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'folderId',
        type: 'string',
        description: 'ID of the folder to ungroup',
        required: true
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { folderId: 'group_123' },
        expectedOutput: createSuccessResult('Entities ungrouped successfully', {
          folderId: 'group_123',
          ungrouped: true
        }),
        description: 'Ungroup a folder',
        context: 'When dissolving a folder grouping'
      }
    ],
    bad: []
  },
  
  performance: {
    expectedDuration: 600,
    complexity: 'high'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    let { folderId } = params;
    
    try {
      // ✅ Resolve llmId to _id if needed
      const resolution = resolveEntityId(folderId);
      
      if (resolution.isLlmId && !resolution.entity) {
        return createErrorResult(
          'Folder not found',
          `No folder found with llmId: ${folderId}`,
          'validation',
          { data: { providedId: folderId } }
        );
      }
      
      folderId = resolution.resolved;
      
      log.info('[entities_ungroup] Ungrouping folder:', { 
        originalId: params.folderId,
        resolvedId: folderId,
        wasLlmId: resolution.isLlmId
      });
      
      const entityStore = useEntityStore.getState();
      await entityStore.actions.ungroupEntities(folderId);
      
      log.info('[entities_ungroup] ✅ Folder ungrouped:', { folderId });
      
      return createSuccessResult(
        'Entities ungrouped and folder removed',
        {
          folderId,
          ungrouped: true
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'entityStore.actions',
            affectedEntities: [folderId]
          } as any,
          ui: {
            refreshContext: true
          }
        }
      );
      
    } catch (error: any) {
      log.error('[entities_ungroup] Error:', error);
      return createErrorResult(
        'Failed to ungroup entities',
        error.message || 'Unknown error',
        'execution',
        {
          data: { folderId },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

